const KEY = "jobenvoy.ready-sound";
let context: AudioContext | undefined;
let memoryEnabled = true;
const CHANGED = "jobenvoy-ready-sound-changed";

export function subscribeReadySound(changed: () => void) {
    window.addEventListener(CHANGED, changed);
    window.addEventListener("storage", changed);
    return () => {
        window.removeEventListener(CHANGED, changed);
        window.removeEventListener("storage", changed);
    };
}

export function readySoundEnabled(): boolean {
    try {
        const saved = localStorage.getItem(KEY);
        return saved === null ? memoryEnabled : saved !== "off";
    } catch {
        return memoryEnabled;
    }
}

export function setReadySoundEnabled(enabled: boolean) {
    memoryEnabled = enabled;
    try {
        localStorage.setItem(KEY, enabled ? "on" : "off");
    } catch {
        /* Keep the preference in memory when storage is unavailable. */
    }
    window.dispatchEvent(new Event(CHANGED));
}

/** The browser's AudioContext, under Safari's old prefixed name where it has only that. */
function audioContextClass(): typeof AudioContext | undefined {
    return (
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    );
}

/**
 * The context, made if there is none or the old one closed, and asked to
 * start. A closed context never plays again; Safari leaves one
 * "interrupted" after a call or another app's audio, and a gesture resumes
 * it. The resume is returned, not awaited: a phone may never settle it.
 */
function wake(): { context: AudioContext; resumed: Promise<void> } | null {
    const Context = audioContextClass();
    if (!Context) return null;
    if (!context || context.state === "closed") context = new Context();
    return {
        context,
        resumed: context.state === "running" ? Promise.resolve() : context.resume(),
    };
}

/**
 * Browsers need a gesture before an asynchronous completion can make a
 * sound. Called from one: a click, a key, or a touch lifting (a touch
 * going down is not one on a phone).
 */
export async function unlockReadySound() {
    if (!readySoundEnabled()) return;
    try {
        await wake()?.resumed;
    } catch {
        /* Sound is optional; the visible notification always works. */
    }
}

/** Two quiet bell notes, with a soft attack and a short fading tail. */
export function playReadySound() {
    if (!readySoundEnabled() || !context || context.state !== "running") return;
    ring(context);
}

/**
 * The ding the person asked to hear, from their tap. Everything happens
 * inside the tap with nothing awaited: a phone lets audio start only within
 * the gesture, and Safari has left a context's resume unsettled. The notes
 * are scheduled on the context's clock, which stands still until it runs,
 * so they sound as soon as it starts.
 */
export function previewReadySound() {
    if (!readySoundEnabled()) return;
    try {
        const woken = wake();
        if (!woken) return;
        woken.resumed.catch(() => {
            /* Sound is optional. */
        });
        ring(woken.context);
    } catch {
        /* Sound is optional; the button must not throw. */
    }
}

function ring(context: AudioContext) {
    try {
        const now = context.currentTime;
        [1046.5, 1568].forEach((frequency, index) => {
            const note = context.createOscillator();
            const volume = context.createGain();
            const start = now + index * 0.11;
            note.type = "sine";
            note.frequency.value = frequency;
            volume.gain.setValueAtTime(0, start);
            volume.gain.linearRampToValueAtTime(0.055, start + 0.012);
            volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.7);
            note.connect(volume);
            volume.connect(context.destination);
            note.onended = () => {
                note.disconnect();
                volume.disconnect();
            };
            note.start(start);
            note.stop(start + 0.75);
        });
    } catch {
        /* An unavailable audio device must not interrupt the app. */
    }
}
